import { AnimationUtils } from '@src/editor/models/AnimationTypes';
import React from 'react';
import styled from 'styled-components';

import { numericalInputStyles } from '../../styles/mainStyle';
import PropertyDropdown from '../common/PropertyDropdown';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface GenericAnimationSettingsProps {
  activeElement: ElementState;
  hideFadeAnimations?: boolean;
  editorStore: EditorStoreLike;
}

export const GenericAnimationSettings: React.FC<
  GenericAnimationSettingsProps
> = props => {
  const { activeElement, hideFadeAnimations, editorStore } = props;

  // Read animations from the editor model (headless timeline) when available,
  // because the renderer state has fade animations stripped (serialized into
  // color_overlay / composition wrappers by FadeAnimationSerializer).
  //
  // Call this fresh inside event handlers rather than closing over the
  // render-time value: commandUpdateElement updates the editor model
  // synchronously, but the panel only re-renders on the debounced renderer
  // sync, so a render-time snapshot goes stale between quick consecutive edits
  // and the second write clobbers the first.
  const getCurrentAnimations = () =>
    editorStore?.editor.getElementById(activeElement?.source?.id)?.animations ??
    activeElement.source.animations ??
    [];

  const animations = getCurrentAnimations();

  const enterAnimationData = animations.find(
    (keyframe: any) => keyframe.time === 'start' || keyframe.time === 0,
  );
  const exitAnimationData = animations.find(
    (keyframe: any) => keyframe.time === 'end',
  );

  const getEnterAnimation = () => {
    const enterAnimation = enterAnimationData?.type ?? 'none';
    const enterAnimationDuration = enterAnimationData?.duration;
    return { animation: enterAnimation, duration: enterAnimationDuration };
  };

  const getExitAnimation = () => {
    const exitAnimation = exitAnimationData?.type ?? 'none';
    const exitAnimationDuration = exitAnimationData?.duration;
    return { animation: exitAnimation, duration: exitAnimationDuration };
  };

  const enterAnimation = getEnterAnimation().animation;
  const exitAnimation = getExitAnimation().animation;

  const enterAnimationDuration = getEnterAnimation().duration;

  const setAnimation = async (
    time: string | number | null,
    type: any,
    duration: number | null = null,
    background_effect: string | null = null,
  ) => {
    // Read fresh at call time (see getCurrentAnimations) — never the
    // render-time `animations` snapshot, which goes stale between quick
    // consecutive edits and would clobber the just-added animation.
    const newAnimations = getCurrentAnimations().filter((keyframe: any) =>
      [0, 'start'].includes(time!)
        ? ![0, 'start'].includes(keyframe.time)
        : keyframe.time !== time,
    );

    if (type !== 'none') {
      if (AnimationUtils.isFadeOverlayType(type)) {
        // Dip and crossfade are stored as native animation types
        newAnimations.push({
          type,
          time: time ?? 'start',
          duration: duration || 1,
        });
      } else {
        const animation: any = { type };

        if (time) {
          animation.time = time;
          animation.duration = duration || 1;
        }

        if (background_effect) {
          animation.background_effect = background_effect;
        }

        if (time === 'end') {
          animation.reversed = true;
        }

        newAnimations.push(animation);
      }
    }

    // Always use commandUpdateElement — no cross-element overlays needed
    if (!activeElement?.source?.id) return;
    editorStore.editor.commandUpdateElement(activeElement.source.id, {
      animations: newAnimations,
      // Clear legacy color_overlay when setting animations natively
      color_overlay: null,
    });
  };

  const getInputValue = async (time: string, duration: string) => {
    let type = time === 'start' ? enterAnimation : exitAnimation;
    let itemDuration = Number(duration);

    if (isNaN(itemDuration)) itemDuration = 1;
    await setAnimation(time, type, itemDuration);
  };

  const animationTypes = hideFadeAnimations
    ? CommonAnimationTypes
    : GenericAnimationTypes;

  return (
    <Content>
      <Item>
        <PropertyCaption>Enter Animation</PropertyCaption>
        <PropertyDropdown
          value={enterAnimation}
          onChange={async value => {
            await setAnimation('start', value, enterAnimationDuration);
          }}
          defaultValue={enterAnimation}
          values={[
            {
              caption: 'None',
              value: 'none',
            },
            ...Object.entries(animationTypes).map(([type, caption]) => ({
              caption: type === 'dip' ? 'Dip from Black' : caption,
              value: type,
            })),
          ]}
        />
      </Item>

      <Item>
        <PropertyCaption>Enter Length</PropertyCaption>
        {/* todo: rework setting current value */}
        <PropertyNumericalInput
          activeElement={activeElement}
          propertyName="start"
          defaultValue={
            enterAnimation === 'dip' || enterAnimation === 'crossfade'
              ? (getEnterAnimation().duration ?? 1)
              : (activeElement?.source?.animations?.find(
                  ({ time }: any) => time === 'start' || time === 0,
                )?.duration ?? '1')
          }
          unit="s"
          getInputValue={getInputValue}
          customStyles={numericalInputStyles}
          disabled={enterAnimation === 'none'}
        />
      </Item>

      <Item>
        <PropertyCaption>Exit Animation</PropertyCaption>

        <PropertyDropdown
          value={exitAnimation}
          onChange={async value => {
            await setAnimation('end', value, enterAnimationDuration);
          }}
          defaultValue={exitAnimation}
          values={[
            {
              caption: 'None',
              value: 'none',
            },
            ...Object.entries(animationTypes).map(([type, caption]) => ({
              caption: type === 'dip' ? 'Dip to Black' : caption,
              value: type,
            })),
          ]}
        />
      </Item>
      <Item>
        <PropertyCaption>Exit Length</PropertyCaption>
        {/* todo: rework setting current value */}
        <PropertyNumericalInput
          activeElement={activeElement}
          propertyName="end"
          defaultValue={
            exitAnimation === 'dip' || exitAnimation === 'crossfade'
              ? (getExitAnimation().duration ?? 1)
              : (activeElement?.source?.animations?.find(
                  ({ time }: any) => time === 'end',
                )?.duration ?? '1')
          }
          unit="s"
          getInputValue={getInputValue}
          customStyles={numericalInputStyles}
          disabled={exitAnimation === 'none'}
        />
      </Item>
    </Content>
  );
};

// Each of these animations has its own options
// For reference: https://github.com/Creatomate/creatomate-node/tree/main/src/animations
const FadeOverlayTypes = {
  dip: 'dip',
  crossfade: 'Cross fade',
};

const CommonAnimationTypes = {
  fade: 'Fade',
  scale: 'Scale',
  slide: 'Slide',
  // 'rotate-slide': 'Rotate Slide',
  pan: 'Pan',
  // wipe: 'Wipe',
  // 'color-wipe': 'Color Wipe',
  // 'circular-wipe': 'Circular Wipe',
  'film-roll': 'Film Roll',
  // squash: 'Squash',
  // spin: 'Spin',
  // stripe: 'Stripe',
  flip: 'Flip',
  // shake: 'Shake',
  // bounce: 'Bounce',
  // wiggle: 'Wiggle',
  shift: 'Shift',
};

const GenericAnimationTypes = {
  ...FadeOverlayTypes,
  ...CommonAnimationTypes,
};

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

// Bottom-align each cell so a caption that wraps to two lines in a narrow
// panel doesn't push its input below its neighbour's.
const Item = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;
